import { format } from 'date-fns';
import type { TCurrentTime } from 'libs/design-system/src/types/time_picker';
import { getLang } from './localStorageUtils';

export const formatToISODate = (date: Date, dateFormat: string= 'yyyy-MM-dd') => {
  const dateObj = new Date(date);
  const formattedDate = format(dateObj, dateFormat);

  return formattedDate;
};

/**
 * Converts 12-hour time format to 24-hour format (HH:mm:ss)
 */
export const convert12To24Hour = (time: TCurrentTime): string => {
  const { hour, minute, ampm } = time;
  let hour24 = Number(hour);
  
  if (ampm === 'PM' && hour24 !== 12) {
    hour24 += 12;
  } else if (ampm === 'AM' && hour24 === 12) {
    hour24 = 0;
  }
  
  return `${String(hour24).padStart(2, '0')}:${minute}:00`;
};

/**
 * Formats a date as ISO date-time with a given timezone or UTC with 'Z' suffix.
 * @param date - The date to format
 * @param timezone - Optional timezone string (e.g., '+03:00'). If not provided or 'Z', formats as UTC with 'Z' suffix
 * @param bound - 'start' sets time to 00:00:00(.000), 'end' sets time to 23:59:59(.999)
 * @param time - Optional time in 12-hour format to use instead of bound default
 * @returns ISO date-time string
 */
export const formatToISODateTimeUTC = (
  date: Date,
  timezone?: string,
  bound?: 'start' | 'end',
  time?: TCurrentTime,
): string => {
  const dateObj = new Date(date);
  const useUTC = !timezone || timezone === 'Z';
  
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  const day = dateObj.getDate();
  
  const datePart = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  
  let timeStr: string;
  if (time && time.hour && time.minute && time.ampm) {
    const time24 = convert12To24Hour(time);
    timeStr = useUTC ? `${time24}.000` : time24;
  } else if (bound === 'end') {
    timeStr = useUTC ? '23:59:59.999' : '23:59:59';
  } else {
    timeStr = useUTC ? '00:00:00.000' : '00:00:00';
  }
  
  const suffix = useUTC ? 'Z' : timezone;
  return `${datePart}T${timeStr}${suffix}`;
};


/**
 * Formats a date in UTC (without timezone conversion) for display
 * @param date - The date to format (should be a UTC date string)
 * @param yearFormat - Optional year format ('short' or 'long')
 * @returns Formatted date string in UTC eg. 10 Feb, 2026
 */
export function formatDateRangeUTC(date: Date, yearFormat?: 'short' | 'long') {
  const monthNames = {
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    ar: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
  };
  
  const lang = getLang();
  const months = monthNames[lang as 'en' | 'ar'] || monthNames.en;
  
  const year = date.getUTCFullYear();
  const month = months[date.getUTCMonth()];
  const day = date.getUTCDate();
  
  if (yearFormat === 'short') {
    const shortYear = String(year).slice(-2);
    return lang === 'ar' ? `${day} ${month} ${shortYear}` : `${month} ${day}, '${shortYear}`;
  } else if (yearFormat === 'long') {
    return lang === 'ar' ? `${day} ${month} ${year}` : `${month} ${day}, ${year}`;
  } else {
    return lang === 'ar' ? `${day} ${month}` : `${month} ${day}`;
  }
}

export const formatToLocalizedDateTime = (dateString: string, lang: "en" | "ar") => {
  const locale = lang === 'ar' ? 'ar-EG' : 'en-GB';
  const dateWithoutOffset = dateString.replace(/([+-]\d{2}:\d{2}|Z)$/, '');
  const date = new Date(`${dateWithoutOffset}Z`);

  const datePart = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);

  const timePart = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  }).format(date);

  return `${datePart}, ${timePart.toUpperCase()}`;
};